import { ValidationError } from './errors.js';
import { roundRatio } from './money.js';

/**
 * The price of a quote line and the totals of a quote version (spec E2 §5.1). The only place where money is
 * multiplied by days, quantities and percentages: the server, the PDF and the live preview on screen all call
 * these functions, so they always agree to the grosz. Everything is integer: prices in grosze, percentages in
 * basis points (100 bp = 1%), days in thousandths (1500 = 1.5 days); one rounding per line (half to even).
 */

export type PricedLineInput = {
  /** Price per day, before the discount. */
  unitPriceGrosze: number;
  /** Quantity in thousandths (500 = 0,5 of a set; F24b). */
  quantityMilli: number;
  /** Days in thousandths, at least 500 (half a day). */
  daysMilli: number;
  /** Linear lines (services) ignore the day scheme: every day 100%. */
  linear: boolean;
  /** 0–10000. */
  discountBp: number;
};

const LINEAR: readonly number[] = [10000];

/**
 * The largest amount a line, a price or a version total may have: amounts are stored as Postgres `integer`
 * (2 147 483 647 grosze = 21 474 836,47 zł). Far above any real quote; beyond it the calculation refuses
 * (Polish `ValidationError`) instead of storing a wrong number.
 */
export const MAX_GROSZE = 2_147_483_647;
const MAX_TEXT = '21 474 836,47 zł';
const LINE_TOO_BIG = `Kwota pozycji przekracza ${MAX_TEXT}`;

function capLine(amount: bigint): number {
  if (amount > BigInt(MAX_GROSZE)) throw new ValidationError([{ path: 'netGrosze', message: LINE_TOO_BIG }]);
  return Number(amount);
}
/** 1000% for one day is plenty; more is surely a typo. */
const MAX_DAY_PCT_BP = 100_000;

type Problem = { path: string; message: string };

const isInt = (v: number) => Number.isSafeInteger(v);

function checkPcts(pctsBp: readonly number[], problems: Problem[]): void {
  if (pctsBp.length === 0) {
    problems.push({ path: 'pctsBp', message: 'Schemat dni musi mieć co najmniej jeden dzień' });
    return;
  }
  pctsBp.forEach((p, i) => {
    if (!isInt(p) || p < 0 || p > MAX_DAY_PCT_BP) problems.push({ path: `pctsBp.${i}`, message: `Procent dnia ${i + 1} musi wynosić od 0% do 1000%` });
  });
}

function checkDays(daysMilli: number, problems: Problem[]): void {
  if (!isInt(daysMilli) || daysMilli < 500) problems.push({ path: 'daysMilli', message: 'Liczba dni musi wynosić co najmniej 0,5' });
}

function checkLine(line: PricedLineInput, problems: Problem[]): void {
  if (!isInt(line.unitPriceGrosze)) problems.push({ path: 'unitPriceGrosze', message: 'Cena musi być liczbą całkowitą groszy' });
  else if (line.unitPriceGrosze < 0) problems.push({ path: 'unitPriceGrosze', message: 'Cena nie może być ujemna' });
  else if (line.unitPriceGrosze > MAX_GROSZE) problems.push({ path: 'unitPriceGrosze', message: `Cena nie może przekraczać ${MAX_TEXT}` });
  if (!isInt(line.quantityMilli)) problems.push({ path: 'quantity', message: 'Ilość może mieć najwyżej trzy miejsca po przecinku' });
  else if (line.quantityMilli < 0) problems.push({ path: 'quantity', message: 'Ilość nie może być ujemna' });
  checkDays(line.daysMilli, problems);
  if (!isInt(line.discountBp) || line.discountBp < 0 || line.discountBp > 10000) problems.push({ path: 'discountBp', message: 'Rabat musi wynosić od 0% do 100%' });
}

function throwIfAny(problems: Problem[]): void {
  if (problems.length > 0) throw new ValidationError(problems);
}

/** Without validation: callers have checked already. */
function factorOf(daysMilli: number, pctsBp: readonly number[]): bigint {
  const pct = (i: number) => BigInt(pctsBp[Math.min(i, pctsBp.length - 1)]!);
  const full = Math.floor(daysMilli / 1000);
  const fraction = BigInt(daysMilli % 1000);
  let sum = 0n;
  // past the end of the list every day costs the same: no need to loop over thousands of days
  const listed = Math.min(full, pctsBp.length);
  for (let i = 0; i < listed; i++) sum += pct(i);
  if (full > listed) sum += BigInt(full - listed) * pct(pctsBp.length - 1);
  return 1000n * sum + fraction * pct(full);
}

/**
 * How many "day percentages" `daysMilli` days are worth, in bp·thousandths: `1000·Σ p[i] (full days) + f·p[n]`,
 * the last percentage applying to every further day. 1 day at 100% = 10 000 000; 1.5 days in [100%, 50%] = 12 500 000.
 */
export function dayFactor(daysMilli: number, pctsBp: readonly number[]): bigint {
  const problems: Problem[] = [];
  checkDays(daysMilli, problems);
  checkPcts(pctsBp, problems);
  throwIfAny(problems);
  return factorOf(daysMilli, pctsBp);
}

/** Net amount of a line in grosze: price × quantity × day factor × (1 − discount), rounded once (half to even). */
export function priceLine(line: PricedLineInput, pctsBp: readonly number[]): number {
  const problems: Problem[] = [];
  checkLine(line, problems);
  checkPcts(pctsBp, problems);
  throwIfAny(problems);
  const factor = factorOf(line.daysMilli, line.linear ? LINEAR : pctsBp);
  const num = BigInt(line.unitPriceGrosze) * BigInt(line.quantityMilli) * factor * BigInt(10000 - line.discountBp);
  return capLine(roundRatio(num, 1000n * 10000n * 1000n * 10000n));
}

/** Price × quantity × days, linear and without discount: what the line would cost "from the price list" (for "W tym rabaty"). */
export function listPriceGrosze(line: PricedLineInput): number {
  const problems: Problem[] = [];
  checkLine(line, problems);
  throwIfAny(problems);
  return capLine(roundRatio(BigInt(line.unitPriceGrosze) * BigInt(line.quantityMilli) * BigInt(line.daysMilli), 1_000_000n));
}

/** Net = the sum of the (already rounded) line amounts; VAT from that sum, rounded half to even; gross = net + VAT. */
export function versionTotals(lineNets: readonly number[], vatBp: number): { netGrosze: number; vatGrosze: number; grossGrosze: number } {
  const problems: Problem[] = [];
  if (!isInt(vatBp) || vatBp < 0 || vatBp > 10000) problems.push({ path: 'vatBp', message: 'Stawka VAT musi wynosić od 0% do 100%' });
  lineNets.forEach((n, i) => {
    if (!isInt(n)) problems.push({ path: `lineNets.${i}`, message: 'Kwota pozycji musi być liczbą całkowitą groszy' });
  });
  throwIfAny(problems);
  const net = lineNets.reduce((a, b) => a + BigInt(b), 0n);
  const vat = roundRatio(net * BigInt(vatBp), 10000n);
  if (net + vat > BigInt(MAX_GROSZE)) throw new ValidationError([{ path: 'grossGrosze', message: `Suma kosztorysu przekracza ${MAX_TEXT}` }]);
  return { netGrosze: Number(net), vatGrosze: Number(vat), grossGrosze: Number(net + vat) };
}

// ---------- equipment / services (F24b, template 2) ----------

/** The two parts of a quote: equipment ("Łącznie urządzenia", "Łącznie po rabacie") and services ("Łącznie usługi"). */
export type QuoteGroup = 'equipment' | 'services';

/** P8: the kind of a quote section: ordinary (equipment) or one of the two fixed ones under the line. */
export type SectionKind = 'equipment' | 'services' | 'transport';

/**
 * The part a section belongs to. The fixed sections "Obsługa" and "Transport" (P8) are services. Any other, from
 * the types of its lines (versions before P8): services when it has a service and no equipment or package;
 * otherwise equipment (also a section of own lines only, or an empty one). Own lines follow their section.
 */
export function sectionGroup(lineTypes: readonly string[], kind: SectionKind = 'equipment'): QuoteGroup {
  if (kind !== 'equipment') return 'services';
  const equipment = lineTypes.some((t) => t === 'item' || t === 'package');
  return !equipment && lineTypes.includes('service') ? 'services' : 'equipment';
}

/** The sections split into the two parts, each in its original order (the screen and the PDF print equipment first). */
export function groupSections<S extends { kind?: SectionKind | string; lines: ReadonlyArray<{ type: string }> }>(sections: readonly S[]): Record<QuoteGroup, S[]> {
  const out: Record<QuoteGroup, S[]> = { equipment: [], services: [] };
  for (const s of sections) out[sectionGroup(s.lines.map((l) => l.type), (s.kind ?? 'equipment') as SectionKind)].push(s);
  return out;
}

export type GroupSummary = {
  /** Σ of each line's amount without its discount (with the day scheme): "Łącznie urządzenia". */
  beforeDiscountGrosze: number;
  /** Σ of the lines' net amounts (each discounted and rounded on its own, as always): "Łącznie po rabacie". */
  netGrosze: number;
  /** Some line with an amount has a discount. */
  discounted: boolean;
  /** The discount every line with an amount has, or null when they differ (or no such line): "(25 %)". */
  commonDiscountBp: number | null;
};

/**
 * A line's amount without its discount (with the day scheme): what the quote PDF prints in the row, the discount
 * standing once under its group (owner 2026-10-31). An accepted „Oferta łączona”'s package line (price = its amount
 * below zero, × 1) is counted as it is.
 */
export function lineFullGrosze(l: PricedLineInput & { netGrosze: number }, pctsBp: readonly number[]): number {
  return l.unitPriceGrosze < 0 ? l.netGrosze : priceLine({ ...l, discountBp: 0 }, pctsBp);
}

/**
 * Only sums: the discount stays on every line (owner 17:35), so the summary rows add up what the lines already say.
 * `netGrosze` of each line is its stored (or previewed) amount; lines worth nothing before the discount (quantity 0,
 * price 0) do not decide the common percentage.
 */
export function groupSummary(lines: ReadonlyArray<PricedLineInput & { netGrosze: number }>, pctsBp: readonly number[]): GroupSummary {
  let before = 0;
  let net = 0;
  const discounts = new Set<number>();
  for (const l of lines) {
    const full = lineFullGrosze(l, pctsBp);
    before += full;
    net += l.netGrosze;
    if (full > 0) discounts.add(l.discountBp);
  }
  const [only] = discounts;
  return { beforeDiscountGrosze: before, netGrosze: net, discounted: [...discounts].some((d) => d > 0), commonDiscountBp: discounts.size === 1 ? only! : null };
}

// ---------- transport (owner remarks 3/22, 3/23) ----------

/**
 * How a transport ("Transport ciężarowy" / "Transport osobowy") is priced: up to `localKm` from the warehouse
 * (Trójmiasto) a flat amount; beyond it a base amount plus a rate per km, the km counted there and back when
 * `roundTrip`. Amounts in grosze, km whole (the event's "ok. N km od magazynu").
 */
export type TransportRule = { flatGrosze: number; baseGrosze: number; perKmGrosze: number; localKm: number; roundTrip: boolean };

/** The price and how it came about: `flat` (also without a distance: `km` null) or `distance` (`billedKm` km at the rate). */
export type TransportCalc = { grosze: number; mode: 'flat' | 'distance'; km: number | null; billedKm: number };

/** Far beyond any road trip; only guards the arithmetic. */
const MAX_KM = 100_000;

function checkRule(rule: TransportRule, km: number | null, problems: Problem[]): void {
  for (const key of ['flatGrosze', 'baseGrosze', 'perKmGrosze'] as const) {
    const v = rule[key];
    if (!isInt(v) || v < 0 || v > MAX_GROSZE) problems.push({ path: key, message: `Kwota transportu musi wynosić od 0 do ${MAX_TEXT}` });
  }
  if (!isInt(rule.localKm) || rule.localKm < 0 || rule.localKm > MAX_KM) problems.push({ path: 'localKm', message: 'Próg ryczałtu to liczba całkowita km od 0' });
  if (km !== null && (!isInt(km) || km < 0 || km > MAX_KM)) problems.push({ path: 'distanceKm', message: 'Odległość to liczba całkowita km od 0' });
}

/** How a transport line is priced: `flat` ("Ryczałt") or `distance` ("Wg km"). */
export type TransportMode = 'flat' | 'distance';

/** The method by the threshold (owner remarks 3/22): flat up to `localKm` (and without a distance), beyond it by km. */
export function transportDefaultMode(rule: Pick<TransportRule, 'localKm'>, distanceKm: number | null): TransportMode {
  return distanceKm === null || distanceKm <= rule.localKm ? 'flat' : 'distance';
}

/**
 * The transport price for an event `distanceKm` from the warehouse (null: unknown, the flat amount). No rounding: all
 * whole numbers. `mode` (spec 2026-10-23-trasa-km-transport): the method chosen by hand on the line; null = by the
 * threshold. By km without a distance is the flat amount (nothing to count).
 */
export function transportCalc(rule: TransportRule, distanceKm: number | null, mode: TransportMode | null = null): TransportCalc {
  const problems: Problem[] = [];
  checkRule(rule, distanceKm, problems);
  throwIfAny(problems);
  if (distanceKm === null || (mode ?? transportDefaultMode(rule, distanceKm)) === 'flat') return { grosze: rule.flatGrosze, mode: 'flat', km: distanceKm, billedKm: 0 };
  const billedKm = distanceKm * (rule.roundTrip ? 2 : 1);
  return { grosze: capLine(BigInt(rule.baseGrosze) + BigInt(billedKm) * BigInt(rule.perKmGrosze)), mode: 'distance', km: distanceKm, billedKm };
}

/** Just the price of `transportCalc`. */
export function transportPrice(rule: TransportRule, distanceKm: number | null): number {
  return transportCalc(rule, distanceKm).grosze;
}

// ---------- costs (F29: rentowność) ----------

/**
 * A quote line's cost for the company (F29): the cost rate per piece per day × quantity (thousandths) × days
 * (thousandths), linear (no day scheme: people are paid every day), one rounding half to even. Not capped: costs are
 * summed, never stored as a line amount.
 */
export function lineCostGrosze(rateGrosze: number, quantityMilli: number, daysMilli: number): number {
  return Number(roundRatio(BigInt(rateGrosze) * BigInt(quantityMilli) * BigInt(daysMilli), 1_000_000n));
}

export type TransportCostInput = {
  perKmGrosze: number;
  perKmMilliGrosze?: number | undefined;
  distanceKm: number | null;
  roundTrip: boolean;
  vehiclesMilli: number;
  /**
   * Owner 2026-10-24 10:22 / 16:15: „Stała kwota za wydarzenie” of the vehicle kind (absent: none) — once for the whole
   * event, not per day („jeden transport liczony na całe wydarzenie”).
   */
  perEventGrosze?: number | undefined;
};

/**
 * Own transport's cost in its two parts (owner 2026-10-24 10:22), the same whether the client pays „Ryczałt” or „Wg km”:
 * cost per km × km (twice with `roundTrip`) × vehicles, + the fixed amount per event × vehicles (owner 16:15: no days
 * factor). Each part rounded once, half to even; null without a distance (the km part is unknown).
 */
export function transportCostParts(input: TransportCostInput): { kmGrosze: number; fixedGrosze: number; grosze: number } | null {
  if (input.distanceKm === null) return null;
  const km = BigInt(input.distanceKm) * (input.roundTrip ? 2n : 1n);
  // review (2026-10-24): a per km from fuel is exact in thousandths of a grosz; one rounding at the end
  const milli = input.perKmMilliGrosze ?? input.perKmGrosze * 1000;
  const kmGrosze = Number(roundRatio(BigInt(milli) * km * BigInt(input.vehiclesMilli), 1_000_000n));
  const fixedGrosze = Number(roundRatio(BigInt(input.perEventGrosze ?? 0) * BigInt(input.vehiclesMilli), 1000n));
  return { kmGrosze, fixedGrosze, grosze: kmGrosze + fixedGrosze };
}

/** Own transport's cost (`transportCostParts`): null without a distance. */
export function transportCostGrosze(input: TransportCostInput): number | null {
  return transportCostParts(input)?.grosze ?? null;
}

/**
 * Own transport's cost per km from fuel (owner 2026-10-24): consumption in ml per km (= l/100 km × 10) × the price
 * per litre, half to even to whole grosze, + the extra per km (tyres, wear, service).
 */
export function fuelPerKmGrosze(input: { mlPerKm: number; priceGroszePerLitre: number; extraPerKmGrosze: number }): number {
  return Number(roundRatio(BigInt(input.mlPerKm) * BigInt(input.priceGroszePerLitre), 1000n)) + input.extraPerKmGrosze;
}

/** The same per km exact, in thousandths of a grosz (ml × grosze per litre = thousandths of a grosz), for the costs. */
export function fuelPerKmMilliGrosze(input: { mlPerKm: number; priceGroszePerLitre: number; extraPerKmGrosze: number }): number {
  return input.mlPerKm * input.priceGroszePerLitre + input.extraPerKmGrosze * 1000;
}

/** A person's cost in the crew: their rate per day × days; null when no rate is known. */
export function crewCostGrosze(rateGrosze: number | null, days: number): number | null {
  return rateGrosze === null ? null : Number(BigInt(rateGrosze) * BigInt(days));
}

/** Margin = result / revenue in basis points (half to even); null when there is no revenue. */
export function marginBp(resultGrosze: number, revenueGrosze: number): number | null {
  if (revenueGrosze <= 0) return null;
  return Number(roundRatio(BigInt(resultGrosze) * 10_000n, BigInt(revenueGrosze)));
}

/** `amount` × part / whole, half to even (`whole` > 0): the share of a planned cost still not covered by the crew. */
export function shareGrosze(amountGrosze: number, part: number, whole: number): number {
  return Number(roundRatio(BigInt(amountGrosze) * BigInt(part), BigInt(whole)));
}
